/**
 * @file     LessonShell.tsx
 * @readme   ./README.md
 * @purpose  Lesson page shell on the page background (no card, no entrance motion): the module and lesson eyebrow, the lesson H1 and standfirst, the lesson body, the done control, and a hairline Previous / Next pair that runs across modules. A locked next lesson opens on its own preview and "Keep reading" gate, whose sign-up returns the reader to that same lesson.
 */
'use client';

import '@/core/i18n/deferred/academy';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens, hubTypographyTokens } from '@/design-system/tokens/typography';
import { eyebrowSx, focusRingSx, hairline, headingFont, hubColors, microLabelSx, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';
import type { JourneyConfig, Lesson, Module } from './types';

const lessonHref = (journey: JourneyConfig, mod: Module, lesson: Lesson) =>
  `/learn-about-bc-real-estate/${journey.slug}/${mod.slug}/${lesson.slug}`;

export interface AdjacentLesson {
  href: string;
  title: string;
}

/** The lessons either side of this one, crossing into the next or previous module. */
export function adjacentLessons(
  journey: JourneyConfig,
  moduleIndex: number,
  lessonIndex: number,
): { prev: AdjacentLesson | null; next: AdjacentLesson | null } {
  const mod = journey.modules[moduleIndex];
  const prevModule = journey.modules[moduleIndex - 1];
  const nextModule = journey.modules[moduleIndex + 1];
  const at = (m: Module, l: Lesson): AdjacentLesson => ({ href: lessonHref(journey, m, l), title: l.titleDefault });
  const prev =
    lessonIndex > 0
      ? at(mod, mod.lessons[lessonIndex - 1])
      : prevModule
        ? at(prevModule, prevModule.lessons[prevModule.lessons.length - 1])
        : null;
  const next =
    lessonIndex < mod.lessons.length - 1
      ? at(mod, mod.lessons[lessonIndex + 1])
      : nextModule
        ? at(nextModule, nextModule.lessons[0])
        : null;
  return { prev, next };
}

interface LessonShellProps {
  journey: JourneyConfig;
  module: Module;
  lesson: Lesson;
  moduleIndex: number;
  lessonIndex: number;
  children: React.ReactNode;
  onComplete?: () => void;
  isCompleted?: boolean;
  readyToComplete?: boolean;
  notReadyHint?: string;
}

const CheckGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

function NavCell({ item, label, align }: { item: AdjacentLesson | null; label: string; align: 'start' | 'end' }) {
  if (!item) return <Box />;
  return (
    <Box
      component={Link}
      href={item.href}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        alignItems: align === 'end' ? 'flex-end' : 'flex-start',
        textAlign: align,
        minHeight: 56,
        py: 2,
        pl: align === 'end' ? 2 : 0,
        pr: align === 'start' ? 2 : 0,
        textDecoration: 'none',
        color: hubColors.textPrimary,
        borderRadius: `${layoutTokens.radius.sm}px`,
        transition: transition('color'),
        '@media (hover: hover)': { '&:hover': { color: hubColors.link } },
        ...focusRingSx,
        ...reducedMotionSx,
      }}
    >
      <Box component="span" sx={microLabelSx}>
        {label}
      </Box>
      <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.medium, lineHeight: 1.35 }}>
        {item.title}
      </Box>
    </Box>
  );
}

export function LessonShell({
  journey,
  module,
  lesson,
  moduleIndex,
  lessonIndex,
  children,
  onComplete,
  isCompleted,
  readyToComplete = true,
  notReadyHint,
}: LessonShellProps) {
  const { t } = useTranslation();
  const { prev, next } = adjacentLessons(journey, moduleIndex, lessonIndex);
  const hint = notReadyHint ?? t('academy.lessonPage.notReady', { defaultValue: 'Finish the lesson to unlock' });

  return (
    <Box component="article" sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 680, width: '100%' }}>
      <Box component="header" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box component="p" sx={eyebrowSx}>
          {t('academy.lessonPage.eyebrow', {
            defaultValue: 'Module {{module}} · Lesson {{n}} of {{total}} · {{count}} min read',
            module: moduleIndex + 1,
            n: lessonIndex + 1,
            total: module.lessons.length,
            count: lesson.estimatedMinutes,
          })}
        </Box>
        <Box
          component="h1"
          sx={{
            m: 0,
            fontFamily: headingFont,
            fontSize: { xs: hubTypographyTokens.h1.mobile, md: 48 },
            fontWeight: hubTypographyTokens.h1.fontWeight,
            letterSpacing: hubTypographyTokens.h1.letterSpacing,
            lineHeight: 1.08,
            color: hubColors.textPrimary,
          }}
        >
          {lesson.titleDefault}
        </Box>
        {lesson.subtitleDefault ? (
          <Box component="p" sx={{ m: 0, fontSize: { xs: 18, md: 20 }, lineHeight: 1.5, color: hubColors.textSecondary }}>
            {lesson.subtitleDefault}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ minWidth: 0 }}>{children}</Box>

      {!isCompleted && onComplete ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={onComplete}
            disabled={!readyToComplete}
            aria-describedby={readyToComplete ? undefined : 'lesson-done-hint'}
            sx={{
              minHeight: 44,
              px: `${layoutTokens.button.md.paddingX}px`,
              borderRadius: `${layoutTokens.radius.md}px`,
              fontSize: 15,
              fontWeight: fontWeightTokens.medium,
              textTransform: 'none',
              backgroundColor: hubColors.interactivePrimary,
              color: hubColors.textOnPrimary,
              '@media (hover: hover)': { '&:hover': { backgroundColor: hubColors.primaryHover } },
              '&.Mui-disabled': { backgroundColor: hubColors.interactivePrimary, color: hubColors.textOnPrimary, opacity: 0.5 },
            }}
          >
            {t('academy.lessonPage.markDone', { defaultValue: 'Mark lesson done' })}
          </Button>
          {!readyToComplete ? (
            <Box component="p" id="lesson-done-hint" aria-live="polite" sx={{ m: 0, fontSize: 14, lineHeight: 1.45, color: hubColors.textMuted }}>
              {hint}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {isCompleted ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box
            role="status"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              minHeight: 44,
              px: 2.5,
              borderRadius: `${layoutTokens.radius.md}px`,
              backgroundColor: hubColors.successSoft,
              color: hubColors.textPrimary,
              fontSize: 15,
              fontWeight: fontWeightTokens.medium,
              '& svg': { color: hubColors.statusSuccess },
            }}
          >
            <CheckGlyph />
            {t('academy.lessonPage.done', { defaultValue: 'Lesson done' })}
          </Box>
          {next ? (
            <Box component="p" sx={{ m: 0, fontSize: 14, color: hubColors.textSecondary }}>
              {t('academy.lessonPage.nextUp', { defaultValue: 'Next up: {{title}}', title: next.title })}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {prev || next ? (
        <Box
          component="nav"
          aria-label={t('academy.lessonPage.navLabel', { defaultValue: 'More lessons' })}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            borderTop: hairline,
            borderBottom: hairline,
            '& > *:nth-of-type(2)': { borderLeft: prev && next ? hairline : 'none' },
          }}
        >
          <NavCell item={prev} label={t('academy.lessonPage.previous', { defaultValue: 'Previous' })} align="start" />
          <NavCell item={next} label={t('academy.lessonPage.next', { defaultValue: 'Next' })} align="end" />
        </Box>
      ) : null}
    </Box>
  );
}
